import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button } from '~/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '~/components/ui/dialog'
import { FormInput } from '~/components/ui/form-items/input'
import { FormSwitch } from '~/components/ui/form-items/switch'
import { useMutation } from '~/hooks/use-mutation'
import { useQueryClientHelper } from '~/hooks/use-query-client'
import { useToast } from '~/hooks/use-toast'
import { type CreateSchoolPayload, type SchoolFormData, schoolSchema } from '~/schemas/school'
import { useAppStore } from '~/stores/app'
import { DialogId } from '~/types/common'
import { MutationKey } from '~/types/mutation-key'
import { QueryKey } from '~/types/query-key'
import type { School } from '~/types/school'

type SchoolResponse = { school: School }

type SchoolFormDialogProps = {
  editingItem?: School | null
}

/**
 * Create/edit dialog for a single school. Create only sends `{ nameEn,
 * nameJa }` (the active toggle isn't shown and the server defaults new
 * schools to active); edit additionally shows the active switch and PATCHes
 * `{ nameEn, nameJa, isActive }` — deactivating a school removes it from the
 * mobile school-search picker for new selections without touching profiles
 * that already stored the name as plain text.
 */
export function SchoolFormDialog({ editingItem }: SchoolFormDialogProps) {
  const { t } = useTranslation()
  const { showSuccess } = useToast()
  const { invalidateQueries } = useQueryClientHelper()
  const openDialogIds = useAppStore((state) => state.openDialogIds)
  const closeDialog = useAppStore((state) => state.closeDialog)

  const isCreateOpen = openDialogIds.includes(DialogId.CREATE_SCHOOL)
  const isUpdateOpen = openDialogIds.includes(DialogId.UPDATE_SCHOOL)
  const isOpen = isCreateOpen || isUpdateOpen
  const isEditing = isUpdateOpen && !!editingItem
  const dialogId = isEditing ? DialogId.UPDATE_SCHOOL : DialogId.CREATE_SCHOOL

  const { control, handleSubmit, reset } = useForm<SchoolFormData>({
    resolver: zodResolver(schoolSchema),
    defaultValues: { nameEn: '', nameJa: '', isActive: true },
  })

  useEffect(() => {
    if (isEditing && editingItem) {
      reset({ nameEn: editingItem.nameEn, nameJa: editingItem.nameJa, isActive: editingItem.isActive })
    } else if (isCreateOpen) {
      reset({ nameEn: '', nameJa: '', isActive: true })
    }
  }, [isEditing, isCreateOpen, editingItem, reset])

  const finalize = () => {
    showSuccess(t('common.save'))
    invalidateQueries(QueryKey.SCHOOLS)
    closeDialog(dialogId)
  }

  const { mutate: createSchool, isPending: isCreating } = useMutation<SchoolResponse, CreateSchoolPayload>({
    mutationKey: MutationKey.CREATE_SCHOOL,
    method: 'POST',
    options: { onSuccess: finalize },
  })

  const { mutate: updateSchool, isPending: isUpdating } = useMutation<SchoolResponse, SchoolFormData>({
    mutationKey: MutationKey.UPDATE_SCHOOL,
    method: 'PATCH',
    idParams: editingItem ? { id: editingItem.id } : undefined,
    options: { onSuccess: finalize },
  })

  const isPending = isCreating || isUpdating

  const onSubmit = (formValues: SchoolFormData) => {
    if (isEditing) {
      updateSchool(formValues)
    } else {
      createSchool({ nameEn: formValues.nameEn, nameJa: formValues.nameJa })
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeDialog(dialogId)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEditing ? t('schools.editSchool') : t('common.create')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <FormInput control={control} name="nameEn" label={t('schools.nameEn')} required disabled={isPending} />
          <FormInput control={control} name="nameJa" label={t('schools.nameJa')} required disabled={isPending} />
          {isEditing && (
            <FormSwitch control={control} name="isActive" label={t('common.active')} disabled={isPending} />
          )}
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => closeDialog(dialogId)} disabled={isPending}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={isPending}>
              {t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
